import { Component } from '@mycolorway/tao';
import Sortable from 'sortablejs';
import { closest, matches } from 'next/javascripts/modules/dom';
import eventListenerMixin from 'next/javascripts/mixins/event-listener-mixin';
import 'next/stylesheets/components/tree.scss';

Component('tr-tree', {
  mixins: [eventListenerMixin],

  properties: {
    sortable: {
      type: Boolean,
      default: false,
    },

    associatedSelect: {
      type: Boolean,
      default: false,
    },

    sorting: {
      type: Boolean,
      default: false,
    },

    sortContainer: {
      type: String,
      default: '',
    },
  },

  connected() {
    this.listenTo(this, {
      'tr-tree-item:selectedChange': this.handleItemSelectedChange.bind(this),
      'tr-tree-item:updateList': this.handleItemUpdateList.bind(this),
    });
  },

  ready() {
    this.initSortable();
  },

  disconnected() {
    this.stopListening();
    if (this.sortable) this.sortables.map(sortable => sortable.destroy());
  },

  getSelectedItems() {
    return this.associatedSelect ? this.getAssociatedSelectedItems()
      : this.querySelectorAll('.tr-tree-item[selected]');
  },

  initSortable() {
    if (!this.sortable) return;
    this.sortables = [...this.querySelectorAll(`${this.sortContainer} .tr-tree-list`)]
      .map(list => new Sortable(list, {
        group: 'tree-item',
        animation: 0,
        delay: 200,
        delayOnTouchOnly: true,
        filter: '.link-toggle-item, .disable-sortable',
        sort: true,
        swapThreshold: 0.25,
        emptyInsertThreshold: 8,
        draggable: '.tr-tree-item',
        forceFallback: (window.gon && window.gon.sortableFallback) || false,
        onStart: this.handleSortableStart.bind(this),
        onEnd: this.handleSortableEnd.bind(this),
        onMove: this.handleSortableMove.bind(this),
      }));
  },

  handleSortableStart() {
    this.sorting = true;
  },

  handleSortableEnd(event) {
    this.sorting = false;

    const {
      item, oldIndex, newIndex, from: previousList, to: targetList,
    } = event;
    this.updateSortedItemDepth(item, targetList);
    this.updateSortedItemParents(previousList, targetList);

    if (oldIndex === newIndex && previousList === targetList) return;
    this.namespacedTrigger('sorted', {
      detail: { item, targetList },
      bubbles: true,
    });
  },

  handleSortableMove(event) {
    if (this.sortableRelatedParent) {
      this.sortableRelatedParent.classList.remove('sortable-related');
    }

    const { related } = event;
    if (matches(related, '.tr-tree-list')) {
      const parent = closest(related, '.tr-tree-item');
      if (parent) {
        parent.classList.add('sortable-related');
        this.sortableRelatedParent = parent;
      }
    }
  },

  updateSortedItemDepth(item, targetList) {
    const targetParent = closest(targetList, '.tr-tree-item');
    const newDepth = targetParent ? targetParent.depth + 1 : 0;

    // eslint-disable-next-line no-param-reassign
    item.depth = newDepth;

    const itemPadding = item.querySelector('.tr-tree-item-content > .tr-tree-item-padding');
    itemPadding.innerHTML = [...Array(newDepth).keys()]
      .map(() => '<div class="padding-item"></div>').join('');
  },

  updateSortedItemParents(previousList, targetList) {
    const previousParent = closest(previousList, '.tr-tree-item');
    if (previousParent && !previousList.querySelector('.tr-tree-item')) {
      previousParent.expandable = false;
      previousParent.expanded = false;
    }

    const targetParent = closest(targetList, '.tr-tree-item');
    if (targetParent && !targetParent.expandable) {
      targetParent.expandable = true;
      targetParent.expanded = true;
    }
  },

  handleItemSelectedChange(event) {
    if (this.associatedSelect) this.refreshAssociatedItems(event.target);
    this.namespacedTrigger('selectedChange', { detail: this.getSelectedItems() });
    event.stopPropagation();
  },

  handleItemUpdateList(event) {
    if (this.associatedSelect) this.refreshDescendantItems(event.target);
  },

  getAssociatedSelectedItems(list = this.querySelector('.tr-tree-list')) {
    let selectedItems = [];
    const items = this.getDirectChildren(list, '.tr-tree-item:not(.tr-tree-loading)');

    Array.prototype.forEach.call(items, (item) => {
      if (item.selected) {
        selectedItems.push(item);
      } else {
        const childList = item.querySelector('.tr-tree-list');
        if (childList) {
          selectedItems = selectedItems.concat(this.getAssociatedSelectedItems(childList));
        }
      }
    });

    return selectedItems;
  },

  refreshAssociatedItems(item) {
    this.refreshDescendantItems(item);
    this.refreshAncestorItems(item);
  },

  refreshDescendantItems(item) {
    Array.prototype.forEach.call(item.querySelectorAll('.tr-tree-item'), (descendant) => {
      // eslint-disable-next-line no-param-reassign
      descendant.selected = item.selected;
    });
  },

  refreshAncestorItems(item) {
    const parentItem = closest(item.parentNode, '.tr-tree-item', this);
    if (!parentItem) return;

    const items = this.getDirectChildren(parentItem, '.tr-tree-list')
      .reduce((result, el) => result.concat(this.getDirectChildren(el, '.tr-tree-item')), []);

    if (items.filter(child => child.selected || child.indeterminate).length === 0) {
      parentItem.selected = false;
      parentItem.indeterminate = false;
    } else if (items.filter(child => !child.selected).length === 0) {
      parentItem.selected = true;
      parentItem.indeterminate = false;
    } else {
      parentItem.selected = false;
      parentItem.indeterminate = true;
    }

    this.refreshAncestorItems(parentItem);
  },

  getDirectChildren(el, selector) {
    return Array.prototype.filter.call(el.children, child => matches(child, selector));
  },
});
